import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import { toast } from 'react-toastify'
import useServerAuth from 'src/customHooks/useServerAuth'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  assignPractitionerToLocation,
  fetchClinicBrandsWithLocations,
  fetchPractitionerClinicAssignments,
  unassignPractitionerFromLocation,
} from 'src/supportPortal/services/clinicWorkforceService'

/**
 * View + edit a screener's clinic-location assignments. Brands group their locations; ticking a
 * location assigns the screener there (the backend auto-attaches the brand), unticking removes it.
 * Editing is restricted to Clinic Success Admin / Manager (the backend further scopes Managers to
 * their own clinics); everyone else sees a read-only view.
 */
export default function ScreenerClinicAssignmentDialog({ open, onClose, practitioner }) {
  const { allRolesInBoolean } = useServerAuth()
  const canEdit = Boolean(
    allRolesInBoolean?.isClinicSuccessAdmin ||
      allRolesInBoolean?.isClinicSuccessManager ||
      allRolesInBoolean?.isAdmin,
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [brands, setBrands] = useState([])
  const [assignedLocationIds, setAssignedLocationIds] = useState(() => new Set())
  const [assignedBrandIds, setAssignedBrandIds] = useState(() => new Set())
  const [savingLocationId, setSavingLocationId] = useState('')
  const [search, setSearch] = useState('')

  const practitionerId = practitioner?.id
  const practitionerName = practitioner?.name || practitioner?.firstName || 'Screener'

  useEffect(() => {
    if (!open || !practitionerId) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [brandList, assignments] = await Promise.all([
          fetchClinicBrandsWithLocations(),
          fetchPractitionerClinicAssignments(practitionerId),
        ])
        if (cancelled) return
        setBrands(brandList)
        setAssignedLocationIds(new Set(assignments.locationIds))
        setAssignedBrandIds(new Set(assignments.brandIds))
      } catch (err) {
        if (!cancelled) setError('Unable to load clinic assignments.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, practitionerId])

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return brands
    return brands
      .map((brand) => {
        const brandMatches = (brand.title || '').toLowerCase().includes(q)
        const locations = brandMatches
          ? brand.locations
          : brand.locations.filter((loc) =>
              [loc.title, loc.address].filter(Boolean).join(' ').toLowerCase().includes(q),
            )
        return { ...brand, locations }
      })
      .filter((brand) => brand.locations.length > 0)
  }, [brands, search])

  async function handleToggleLocation(brand, location, nextChecked) {
    if (!practitionerId || !location?.id) return
    const payload = { practitionerId, role: 'screener', clinicLocationId: location.id }
    setSavingLocationId(location.id)
    try {
      const resp = nextChecked
        ? await assignPractitionerToLocation(payload)
        : await unassignPractitionerFromLocation(payload)
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        setAssignedLocationIds((prev) => {
          const next = new Set(prev)
          if (nextChecked) next.add(location.id)
          else next.delete(location.id)
          return next
        })
        if (nextChecked) {
          setAssignedBrandIds((prev) => new Set(prev).add(brand.id))
        }
        toast.success(
          nextChecked
            ? `Assigned to ${location.title}`
            : `Removed from ${location.title}`,
        )
      } else {
        const message = resp?.data?.message
        toast.error(
          typeof message === 'string'
            ? message
            : 'Unable to update this assignment (you may not manage this clinic).',
        )
      }
    } catch (err) {
      toast.error('Unable to update this assignment.')
    } finally {
      setSavingLocationId('')
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Clinic Assignments
        <Typography variant="body2" color="text.secondary">
          {practitionerName}
          {!canEdit && ' · read-only'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Editing clinic assignments is restricted to Clinic Success Admin and the attached Clinic
            Success Manager.
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          label="Search clinics"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ mb: 1.5 }}
        />
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : filteredBrands.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No clinics found.
          </Typography>
        ) : (
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {filteredBrands.map((brand) => (
              <Box key={brand.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {brand.title}
                  </Typography>
                  {assignedBrandIds.has(brand.id) && (
                    <Chip size="small" color="success" variant="outlined" label="On brand" />
                  )}
                </Stack>
                <Stack sx={{ pl: 1 }}>
                  {brand.locations.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No locations.
                    </Typography>
                  ) : (
                    brand.locations.map((location) => {
                      const checked = assignedLocationIds.has(location.id)
                      const isSaving = savingLocationId === location.id
                      return (
                        <FormControlLabel
                          key={location.id}
                          sx={{ alignItems: 'flex-start', m: 0, py: 0.25 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={!canEdit || isSaving}
                              onChange={(event) =>
                                handleToggleLocation(brand, location, event.target.checked)
                              }
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                              <LocationOnIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                              <Box>
                                <Typography variant="body2">{location.title}</Typography>
                                {location.address && (
                                  <Typography variant="caption" color="text.secondary">
                                    {location.address}
                                  </Typography>
                                )}
                              </Box>
                              {isSaving && <CircularProgress size={14} sx={{ ml: 1 }} />}
                            </Box>
                          }
                        />
                      )
                    })
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
